import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { api, extractError } from '@/lib/api.js';
import { SOLUTIONS, type Solution } from '@/lib/solutions.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';

function SolutionLogoPlaceholder() {
  return (
    <div
      style={{
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg3)',
        borderRadius: 8,
        border: '1px dashed var(--border)',
      }}
    >
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text3)' }}>
        [Logo em breve]
      </span>
    </div>
  );
}

function SolutionCard({ solution }: { solution: Solution }) {
  const interest = useMutation({
    mutationFn: async (body: { solution_id: string; solution_name: string }) => {
      const r = await api.post<{ success: boolean }>('/solutions/interest', body);
      return r.data;
    },
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function handleInterest() {
    setLoading(true);
    setFeedback(null);
    try {
      await interest.mutateAsync({
        solution_id: solution.id,
        solution_name: solution.name,
      });
      setFeedback('Seu interesse foi registrado. Em breve nossa equipe entrará em contato.');
      window.open(solution.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setFeedback(extractError(err).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <article
      style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <SolutionLogoPlaceholder />

      <h2
        style={{
          margin: 0,
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          fontSize: 16,
          color: 'var(--text)',
        }}
      >
        {solution.name}
      </h2>

      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          color: 'var(--text2)',
          lineHeight: 1.6,
          flex: 1,
        }}
      >
        {solution.description}
      </p>

      <a
        href={solution.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          color: 'var(--text3)',
          textDecoration: 'none',
        }}
      >
        {solution.url.replace(/^https?:\/\//, '')}
        <ExternalLink size={12} strokeWidth={1.5} />
      </a>

      {feedback ? (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text2)' }}>{feedback}</p>
      ) : null}

      <button
        type="button"
        onClick={() => void handleInterest()}
        disabled={loading}
        style={{
          width: '100%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '10px 16px',
          border: 'none',
          borderRadius: 8,
          background: 'var(--primary)',
          color: '#000',
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          fontSize: 13,
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? (
          <>
            <Loader2 size={16} strokeWidth={1.5} className="animate-spin" />
            Registrando…
          </>
        ) : (
          'Quero saber mais'
        )}
      </button>
    </article>
  );
}

export function EcossistemaPage() {
  return (
    <div>
      <header style={{ marginBottom: 0 }}>
        <h1
          style={{
            margin: '0 0 4px',
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: 20,
            color: 'var(--text)',
          }}
        >
          {NAV_LABELS.solucoesHack}
        </h1>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text2)',
          }}
        >
          Soluções do grupo Hack Growth Network para acelerar você e sua empresa.
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SOLUTIONS.map((solution) => (
          <SolutionCard key={solution.id} solution={solution} />
        ))}
      </div>
    </div>
  );
}
